import React, { useCallback, useEffect, useState } from 'react';
import {
  MdArrowBack, MdCheckCircle, MdBlock, MdPersonAdd, MdLockReset,
  MdRefresh, MdContentCopy, MdAdminPanelSettings, MdClose,
} from 'react-icons/md';
import {
  adminListUsers, adminApproveUser, adminRevokeUser,
  adminCreateUser, adminResetPassword, adminDismissReset,
} from '../services/backendAPI';

// Readable temp password: two short words + digits, easy to read out / type once.
const WORDS = ['maple', 'river', 'cedar', 'otter', 'amber', 'delta', 'cobalt', 'lunar', 'harbor', 'quartz'];
function suggestTempPassword() {
  // No Math.random dependence on determinism here — UI-only convenience.
  const w = WORDS[Math.floor(Math.random() * WORDS.length)];
  const n = 100 + Math.floor(Math.random() * 900);
  return `${w}-${n}`;
}

const STATUS_TINT = {
  approved: 'var(--td-green, #008a0d)',
  pending:  'var(--td-amber, #f59e0b)',
  revoked:  'var(--td-red, #d32f2f)',
};

const AdminScreen = ({ onNavigate, onBack }) => {
  const [data, setData]       = useState({ accounts: [], orphans: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(null);   // username currently being acted on
  const [flash, setFlash]     = useState(null);   // { type, text }

  // Temp-password modal: { mode: 'create'|'reset', username, role, password }
  const [pwModal, setPwModal] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const res = await adminListUsers();
    setLoading(false);
    if (!res.success) { setError(res.error || 'Could not load users.'); return; }
    setError(null);
    setData({ accounts: res.accounts || [], orphans: res.orphans || [] });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const note = (type, text) => { setFlash({ type, text }); setTimeout(() => setFlash(null), 4000); };

  const act = async (username, fn, okMsg) => {
    setBusy(username);
    const res = await fn();
    setBusy(null);
    if (!res.success) { note('error', res.error || 'Action failed.'); return; }
    note('ok', okMsg);
    refresh();
  };

  const openPwModal = (mode, username, role = 'guest') =>
    setPwModal({ mode, username, role, password: suggestTempPassword() });

  const submitPwModal = async () => {
    const { mode, username, role, password } = pwModal;
    if (!password || password.length < 6) { note('error', 'Temp password must be at least 6 characters.'); return; }
    setBusy(username || '(new)');
    const res = mode === 'create'
      ? await adminCreateUser(username, password, role)
      : await adminResetPassword(username, password);
    setBusy(null);
    if (!res.success) { note('error', res.error || 'Action failed.'); return; }
    note('ok', mode === 'create'
      ? `Created ${username}. Temp password: ${password} — they must change it on first login.`
      : `Reset ${username}. Temp password: ${password} — they must change it on next login.`);
    setPwModal(null);
    refresh();
  };

  const copyPw = () => { try { navigator.clipboard?.writeText(pwModal.password); } catch {} };

  const pending = data.accounts.filter(u => u.status === 'pending');
  const resetRequests = data.accounts.filter(u => u.resetRequested);

  return (
    <div className="screen bh-flow admin-screen">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('profileScreen'))} aria-label="Back">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title"><MdAdminPanelSettings style={{ verticalAlign: '-3px', marginRight: 6 }} />Admin</h1>
        </div>
        <button className="bh-flow-back" onClick={refresh} aria-label="Refresh" title="Refresh">
          <MdRefresh />
        </button>
      </header>

      <div className="bh-flow-body">
        {flash && (
          <div className={flash.type === 'error' ? 'login-error' : 'login-notice'} role="status"
               style={{ marginBottom: 'var(--space-md)' }}>{flash.text}</div>
        )}
        {error && <div className="login-error" role="alert">{error}</div>}
        {loading && <p className="bh-muted">Loading users…</p>}

        {/* Pending approval queue */}
        {pending.length > 0 && (
          <section>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Awaiting approval ({pending.length})</h2>
            <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
              {pending.map((u, i) => (
                <UserRow key={u.username} u={u} last={i === pending.length - 1}>
                  <button className="bh-pill-btn" disabled={busy === u.username}
                          onClick={() => act(u.username, () => adminApproveUser(u.username), `Approved ${u.username}.`)}>
                    <MdCheckCircle /> Approve
                  </button>
                  <button className="bh-pill-btn danger" disabled={busy === u.username}
                          onClick={() => act(u.username, () => adminRevokeUser(u.username), `Revoked ${u.username}.`)}>
                    <MdBlock /> Revoke
                  </button>
                </UserRow>
              ))}
            </div>
          </section>
        )}

        {/* Password reset requests — users who hit "Forgot password?" on login.
            Generate a temp password (relay it out-of-band), or dismiss a bogus one. */}
        {resetRequests.length > 0 && (
          <section>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Password reset requests ({resetRequests.length})</h2>
            <p className="bh-muted" style={{ marginTop: 0 }}>
              These people asked to reset their password. Set a temp password and share it
              with them directly — they’ll be forced to choose a new one on next login.
            </p>
            <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
              {resetRequests.map((u, i) => (
                <UserRow key={u.username} u={u} last={i === resetRequests.length - 1}
                         sub={u.resetRequestedAt ? `requested ${u.resetRequestedAt.slice(0, 10)}` : undefined}>
                  <button className="bh-pill-btn" disabled={busy === u.username}
                          onClick={() => openPwModal('reset', u.username, u.role)}>
                    <MdLockReset /> Set temp pw
                  </button>
                  <button className="bh-pill-btn" disabled={busy === u.username}
                          onClick={() => act(u.username, () => adminDismissReset(u.username), `Dismissed reset request for ${u.username}.`)}>
                    <MdClose /> Dismiss
                  </button>
                </UserRow>
              ))}
            </div>
          </section>
        )}

        {/* Orphans — logged in on the old shared password, no account yet */}
        {data.orphans.length > 0 && (
          <section>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>No account yet ({data.orphans.length})</h2>
            <p className="bh-muted" style={{ marginTop: 0 }}>
              These people signed in on the old shared password but have no account, so they
              can’t log in now. Create one with a temp password to let them back in.
            </p>
            <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
              {data.orphans.map((o, i) => (
                <UserRow key={o.username} u={{ username: o.username, status: 'no account' }}
                         sub={`${o.logins} login${o.logins === 1 ? '' : 's'}${o.lastSeen ? ` · last ${o.lastSeen.slice(0, 10)}` : ''}`}
                         last={i === data.orphans.length - 1}>
                  <button className="bh-pill-btn" disabled={busy === o.username}
                          onClick={() => openPwModal('create', o.username, 'guest')}>
                    <MdPersonAdd /> Create + temp pw
                  </button>
                </UserRow>
              ))}
            </div>
          </section>
        )}

        {/* All accounts */}
        <section>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>All accounts ({data.accounts.length})</h2>
            <button className="bh-pill-btn" onClick={() => openPwModal('create', '', 'guest')}>
              <MdPersonAdd /> New user
            </button>
          </div>
          <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
            {data.accounts.map((u, i) => (
              <UserRow key={u.username} u={u} last={i === data.accounts.length - 1}
                       sub={`${u.role}${u.mustChangePassword ? ' · must change pw' : ''}${!u.hasPassword ? ' · no password set' : ''}`}>
                {u.status === 'revoked' ? (
                  <button className="bh-pill-btn" disabled={busy === u.username}
                          onClick={() => act(u.username, () => adminApproveUser(u.username), `Re-enabled ${u.username}.`)}>
                    <MdCheckCircle /> Re-enable
                  </button>
                ) : (
                  <button className="bh-pill-btn danger" disabled={busy === u.username}
                          onClick={() => act(u.username, () => adminRevokeUser(u.username), `Revoked ${u.username}.`)}>
                    <MdBlock /> Revoke
                  </button>
                )}
                <button className="bh-pill-btn" disabled={busy === u.username}
                        onClick={() => openPwModal('reset', u.username, u.role)}>
                  <MdLockReset /> Reset pw
                </button>
              </UserRow>
            ))}
            {!loading && data.accounts.length === 0 && <div className="bh-row"><span className="bh-muted">No accounts.</span></div>}
          </div>
        </section>
      </div>

      {/* Temp-password modal (create / reset) */}
      {pwModal && (
        <div className="login-page change-pw-overlay" onClick={() => setPwModal(null)}>
          <div className="login-box" onClick={e => e.stopPropagation()}>
            <h1>{pwModal.mode === 'create' ? 'Create account' : 'Reset password'}</h1>
            <p className="login-subtitle">
              {pwModal.mode === 'create'
                ? 'New account with a temporary password. They must change it on first login.'
                : `Temporary password for ${pwModal.username}. They must change it on next login.`}
            </p>
            {pwModal.mode === 'create' && (
              <>
                <label htmlFor="adminNewUser">Username (email)</label>
                <input className="bh-input" id="adminNewUser" type="text" autoComplete="off" placeholder="name@td.com"
                       value={pwModal.username} onChange={e => setPwModal(m => ({ ...m, username: e.target.value }))} />
                <label htmlFor="adminNewRole">Role</label>
                <select className="bh-input" id="adminNewRole" value={pwModal.role}
                        onChange={e => setPwModal(m => ({ ...m, role: e.target.value }))}>
                  <option value="guest">guest</option>
                  <option value="admin">admin</option>
                </select>
              </>
            )}
            <label htmlFor="adminTempPw">Temporary password</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="bh-input" id="adminTempPw" type="text" autoComplete="off" style={{ flex: 1 }}
                     value={pwModal.password} onChange={e => setPwModal(m => ({ ...m, password: e.target.value }))} />
              <button className="bh-pill-btn" type="button" onClick={copyPw} title="Copy"><MdContentCopy /></button>
              <button className="bh-pill-btn" type="button"
                      onClick={() => setPwModal(m => ({ ...m, password: suggestTempPassword() }))} title="Suggest another">
                <MdRefresh />
              </button>
            </div>
            <button className="bh-btn-primary login-submit" type="button" disabled={busy != null} onClick={submitPwModal}>
              {pwModal.mode === 'create' ? 'Create account' : 'Set temp password'}
            </button>
            <button className="login-link-btn" type="button" style={{ marginTop: 'var(--space-sm)' }}
                    onClick={() => setPwModal(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// One user line: identity on the left, status chip + action buttons on the right.
const UserRow = ({ u, sub, last, children }) => (
  <div className="bh-row" style={{
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
    borderBottom: last ? 'none' : '1px solid var(--border-subtle, rgba(128,128,128,0.18))',
    flexWrap: 'wrap',
  }}>
    <div style={{ flex: '1 1 180px', minWidth: 0 }}>
      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.username}</div>
      <div className="bh-muted" style={{ fontSize: 12 }}>
        <span style={{ color: STATUS_TINT[u.status] || 'var(--text-light)' }}>{u.status}</span>
        {sub ? ` · ${sub}` : ''}
      </div>
    </div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{children}</div>
  </div>
);

export default AdminScreen;
